import React, { useState, useEffect } from 'react';
import {
  Button,
  Snackbar,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Bookmark as BookmarkIcon,
  BookmarkBorder as BookmarkBorderIcon
} from '@mui/icons-material';

const BookmarkButton = ({ 
  itemId,
  itemType = 'university', // 'university', 'program', 'article' 等
  title = '',
  description = '',
  image = '',
  children,
  variant = 'outlined',
  size = 'medium',
  showText = true,
  onBookmarkChange = null,
  ...props 
}) => {
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  // 从 localStorage 获取收藏状态
  useEffect(() => {
    const bookmarks = getBookmarks();
    const bookmarkKey = `${itemType}_${itemId}`;
    setIsBookmarked(bookmarks.has(bookmarkKey));
  }, [itemId, itemType]);

  // 获取所有收藏
  const getBookmarks = () => {
    try {
      const bookmarks = localStorage.getItem('bookmarks');
      return bookmarks ? new Set(JSON.parse(bookmarks)) : new Set();
    } catch (error) {
      console.error('获取收藏失败:', error);
      return new Set();
    }
  };

  // 保存收藏到 localStorage
  const saveBookmarks = (bookmarks) => {
    try {
      localStorage.setItem('bookmarks', JSON.stringify([...bookmarks]));
    } catch (error) {
      console.error('保存收藏失败:', error);
    }
  };

  // 获取收藏详情
  const getBookmarkDetails = () => {
    try {
      const details = localStorage.getItem('bookmarkDetails');
      return details ? JSON.parse(details) : {};
    } catch (error) {
      console.error('获取收藏详情失败:', error);
      return {};
    }
  };

  // 保存收藏详情
  const saveBookmarkDetails = (details) => {
    try {
      localStorage.setItem('bookmarkDetails', JSON.stringify(details));
    } catch (error) {
      console.error('保存收藏详情失败:', error);
    }
  };

  // 显示提示消息
  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // 处理收藏/取消收藏
  const handleBookmarkToggle = async () => {
    if (loading) return;

    setLoading(true);
    
    try {
      const bookmarks = getBookmarks();
      const bookmarkKey = `${itemType}_${itemId}`;
      const bookmarkDetails = getBookmarkDetails();
      
      if (isBookmarked) {
        // 取消收藏
        bookmarks.delete(bookmarkKey);
        delete bookmarkDetails[bookmarkKey];
        setIsBookmarked(false);
        showSnackbar('已取消收藏');
      } else {
        // 添加收藏
        bookmarks.add(bookmarkKey);
        bookmarkDetails[bookmarkKey] = {
          id: itemId,
          type: itemType,
          title,
          description,
          image,
          url: window.location.href,
          bookmarkedAt: new Date().toISOString()
        };
        setIsBookmarked(true);
        showSnackbar('已添加到收藏');
      }

      // 保存到 localStorage
      saveBookmarks(bookmarks);
      saveBookmarkDetails(bookmarkDetails);

      // 触发回调
      if (onBookmarkChange) {
        onBookmarkChange(isBookmarked ? 'removed' : 'added', {
          id: itemId,
          type: itemType,
          title,
          description,
          image
        });
      }

    } catch (error) {
      console.error('收藏操作失败:', error);
      showSnackbar('操作失败，请重试', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // 如果提供了自定义子组件，则渲染子组件
  if (children) {
    return (
      <>
        {React.cloneElement(children, {
          onClick: handleBookmarkToggle,
          disabled: loading,
          ...props
        })}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={handleSnackbarClose} severity={snackbarSeverity}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </>
    );
  }

  // 默认按钮样式
  return (
    <>
      <Button
        variant={variant}
        size={size}
        startIcon={isBookmarked ? <BookmarkIcon /> : <BookmarkBorderIcon />}
        onClick={handleBookmarkToggle}
        disabled={loading}
        sx={{
          color: isBookmarked ? 'primary.main' : 'text.secondary',
          '&:hover': {
            color: 'primary.main',
            backgroundColor: 'primary.50'
          }
        }}
        {...props}
      >
        {showText && (isBookmarked ? '已收藏' : '收藏')}
      </Button>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

// 收藏管理工具函数
export const bookmarkUtils = {
  // 获取所有收藏
  getAllBookmarks: () => {
    try {
      const bookmarks = localStorage.getItem('bookmarks');
      const details = localStorage.getItem('bookmarkDetails');
      return {
        bookmarks: bookmarks ? JSON.parse(bookmarks) : [],
        details: details ? JSON.parse(details) : {}
      };
    } catch (error) {
      console.error('获取收藏失败:', error);
      return { bookmarks: [], details: {} };
    }
  },

  // 清空所有收藏
  clearAllBookmarks: () => {
    try {
      localStorage.removeItem('bookmarks');
      localStorage.removeItem('bookmarkDetails');
      return true;
    } catch (error) {
      console.error('清空收藏失败:', error);
      return false;
    }
  },

  // 检查是否已收藏
  isBookmarked: (itemId, itemType) => {
    try {
      const bookmarks = localStorage.getItem('bookmarks');
      if (!bookmarks) return false;
      const bookmarkSet = new Set(JSON.parse(bookmarks));
      return bookmarkSet.has(`${itemType}_${itemId}`);
    } catch (error) {
      console.error('检查收藏状态失败:', error);
      return false;
    }
  },

  // 按类型获取收藏
  getBookmarksByType: (type) => {
    try {
      const { bookmarks, details } = bookmarkUtils.getAllBookmarks();
      return bookmarks
        .filter(key => key.startsWith(`${type}_`))
        .map(key => details[key])
        .filter(Boolean);
    } catch (error) {
      console.error('按类型获取收藏失败:', error);
      return [];
    }
  }
};

export default BookmarkButton;

